import React, { useEffect, useRef } from "react";

function randomBetween(min, max) {
  return min + Math.random() * (max - min);
}

function randomInt(min, max) {
  return Math.floor(randomBetween(min, max + 1));
}

function getAdaptiveParticleCount(width) {
  if (width < 480) return randomInt(30, 60);
  if (width <= 900) return randomInt(60, 120);
  return randomInt(120, 200);
}

function createEmber(width, height) {
  return {
    x: Math.random() * width,
    y: Math.random() * height,
    vx: randomBetween(-0.12, 0.12),
    vy: randomBetween(-0.85, -0.25),
    radius: randomBetween(0.9, 3.2),
    alpha: randomBetween(0.28, 0.72),
    twinkleSpeed: randomBetween(0.002, 0.006),
    twinklePhase: randomBetween(0, Math.PI * 2),
    driftSeed: randomBetween(0, Math.PI * 2),
  };
}

export function ParticleField({ type, theme }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const particlesRef = useRef([]);
  const contextRef = useRef(null);
  const sizeRef = useRef({ width: 0, height: 0, dpr: 1 });

  useEffect(() => {
    if (type !== "embers") {
      return undefined;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const context = canvas.getContext("2d", { alpha: true });
    if (!context) {
      return undefined;
    }

    contextRef.current = context;

    const setCanvasSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      sizeRef.current = { width, height, dpr };
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      const particleCount = getAdaptiveParticleCount(width);
      particlesRef.current = Array.from({ length: particleCount }, () => createEmber(width, height));
    };

    const resetParticle = (particle) => {
      const { width, height } = sizeRef.current;
      particle.x = Math.random() * width;
      particle.y = height + randomBetween(8, 60);
      particle.vx = randomBetween(-0.12, 0.12);
      particle.vy = randomBetween(-0.85, -0.25);
      particle.radius = randomBetween(0.9, 3.2);
      particle.alpha = randomBetween(0.28, 0.72);
      particle.twinkleSpeed = randomBetween(0.002, 0.006);
      particle.twinklePhase = randomBetween(0, Math.PI * 2);
      particle.driftSeed = randomBetween(0, Math.PI * 2);
    };

    const draw = (time) => {
      const ctx = contextRef.current;
      const { width, height } = sizeRef.current;
      if (!ctx || width === 0 || height === 0) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }

      ctx.clearRect(0, 0, width, height);

      const glowBase = theme?.ai?.orbGlow || "rgba(255,110,40,0.45)";

      for (const particle of particlesRef.current) {
        particle.x += particle.vx + Math.sin(time * 0.0008 + particle.driftSeed) * 0.08;
        particle.y += particle.vy;
        particle.twinklePhase += particle.twinkleSpeed * 16;

        if (
          particle.y < -18 ||
          particle.x < -28 ||
          particle.x > width + 28
        ) {
          resetParticle(particle);
        }

        const twinkle = 0.7 + Math.sin(particle.twinklePhase) * 0.3;
        const alpha = Math.max(0.08, particle.alpha * twinkle);

        const gradient = ctx.createRadialGradient(
          particle.x,
          particle.y,
          0,
          particle.x,
          particle.y,
          particle.radius * 3.6
        );
        gradient.addColorStop(0, `rgba(255, 180, 120, ${alpha})`);
        gradient.addColorStop(0.45, `rgba(255, 130, 60, ${alpha * 0.9})`);
        gradient.addColorStop(1, "rgba(255, 100, 20, 0)");

        ctx.beginPath();
        ctx.fillStyle = gradient;
        ctx.arc(particle.x, particle.y, particle.radius * 3.6, 0, Math.PI * 2);
        ctx.fill();

        ctx.beginPath();
        ctx.fillStyle = glowBase;
        ctx.arc(particle.x, particle.y, particle.radius * 0.9, 0, Math.PI * 2);
        ctx.globalAlpha = Math.min(1, alpha + 0.2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    let resizeRaf = 0;
    const handleResize = () => {
      if (resizeRaf) return;
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = 0;
        setCanvasSize();
      });
    };

    setCanvasSize();
    rafRef.current = requestAnimationFrame(draw);
    window.addEventListener("resize", handleResize);

    return () => {
      if (resizeRaf) {
        cancelAnimationFrame(resizeRaf);
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      window.removeEventListener("resize", handleResize);

      const ctx = contextRef.current;
      if (ctx) {
        const { width, height } = sizeRef.current;
        ctx.clearRect(0, 0, width, height);
      }

      canvas.width = 0;
      canvas.height = 0;
      particlesRef.current = [];
      contextRef.current = null;
    };
  }, [theme, type]);

  if (type !== "embers") {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      className="tl-v3-particle-canvas"
      aria-hidden="true"
    />
  );
}
